import type {
  AIAssistUsage,
  AICreditLedger,
  AITool,
  AIUseCase,
  ActionItem,
  AppSettings,
  BaseRecord,
  BusinessProfile,
  ExportHistory,
  GeneratedDocument,
  ImportHistory,
  LicenseActivationCache,
  LiteracyRecord,
  ModuleStatus,
  PrivacyGateEvent,
  ReadinessScore,
  RiskAssessment,
  RoleCheckRecord,
  TransparencyCheck,
  Workspace
} from "../domain/types";
import { APP_VERSION, RULES_VERSION, SCHEMA_VERSION } from "../domain/types";
import { now, uid } from "../domain/rules";

type SQLiteWorkerPromiser = (type: string, args?: Record<string, unknown>) => Promise<{
  dbId?: string;
  result?: {
    filename?: string;
    resultRows?: Array<Record<string, unknown>>;
    [key: string]: unknown;
  };
}>;

declare global {
  interface Window {
    sqlite3Worker1Promiser?: (options: { worker?: () => Worker; onready?: () => void }) => SQLiteWorkerPromiser;
  }
}
type RecordWithId = BaseRecord;
type TableName =
  | "businessProfile"
  | "aiTools"
  | "aiUseCases"
  | "riskAssessments"
  | "actionItems"
  | "roleCheckRecords"
  | "literacyRecords"
  | "transparencyChecks"
  | "generatedDocuments"
  | "moduleStatuses"
  | "readinessScores"
  | "privacyGateEvents"
  | "aiAssistUsage"
  | "exportHistory"
  | "importHistory"
  | "aiCreditLedger"
  | "licenseActivations"
  | "appSettings";

const OPFS_DATABASE_FILENAME = "file:ai-act-readiness-kit.sqlite3?vfs=opfs";
const WASM_FALLBACK_DATABASE_FILENAME = ":memory:";
const TABLE_NAMES: TableName[] = [
  "businessProfile",
  "aiTools",
  "aiUseCases",
  "riskAssessments",
  "actionItems",
  "roleCheckRecords",
  "literacyRecords",
  "transparencyChecks",
  "generatedDocuments",
  "moduleStatuses",
  "readinessScores",
  "privacyGateEvents",
  "aiAssistUsage",
  "exportHistory",
  "importHistory",
  "aiCreditLedger",
  "licenseActivations",
  "appSettings"
];

const SQL_TABLES = TABLE_NAMES.map((name) => `
  CREATE TABLE IF NOT EXISTS ${name} (
    id TEXT PRIMARY KEY NOT NULL,
    json TEXT NOT NULL,
    updated_at TEXT
  );
`).join("\n");

let promiserPromise: Promise<SQLiteWorkerPromiser> | undefined;
let dbIdPromise: Promise<string> | undefined;
let openedFilename = "";
let activeStorageMode: AppSettings["storage_mode"] = "sqlite-wasm-fallback";

async function getPromiser() {
  if (!promiserPromise) {
    promiserPromise = new Promise<SQLiteWorkerPromiser>((resolve, reject) => {
      const createPromiser = window.sqlite3Worker1Promiser;
      if (!createPromiser) {
        reject(new Error("SQLite Worker Promiser wurde nicht geladen."));
        return;
      }
      const promiser = createPromiser({
        worker: () => new Worker("/sqlite/sqlite3-worker1-bundler-friendly.mjs", { type: "module" }),
        onready: () => resolve(promiser)
      });
    });
  }
  return promiserPromise;
}

async function getDbId() {
  if (!dbIdPromise) {
    dbIdPromise = (async () => {
      const promiser = await getPromiser();
      const openResponse = await openDatabase(promiser);
      await rawExec(promiser, openResponse.dbId, "PRAGMA journal_mode=WAL;");
      await rawExec(promiser, openResponse.dbId, "PRAGMA foreign_keys=ON;");
      await rawExec(promiser, openResponse.dbId, SQL_TABLES);
      return openResponse.dbId;
    })();
  }
  return dbIdPromise;
}

async function openDatabase(promiser: SQLiteWorkerPromiser) {
  try {
    const opfsResponse = await promiser("open", { filename: OPFS_DATABASE_FILENAME });
    if (!opfsResponse.dbId) throw new Error("SQLite/OPFS Datenbank konnte nicht ge�ffnet werden.");
    openedFilename = String(opfsResponse.result?.filename ?? OPFS_DATABASE_FILENAME);
    activeStorageMode = "sqlite-opfs";
    return { dbId: opfsResponse.dbId };
  } catch (opfsError) {
    const fallbackResponse = await promiser("open", { filename: WASM_FALLBACK_DATABASE_FILENAME }).catch((fallbackError) => {
      throw new Error(`SQLite-WASM-Fallback konnte nicht ge�ffnet werden: ${errorMessage(fallbackError)}; OPFS-Fehler: ${errorMessage(opfsError)}`);
    });
    if (!fallbackResponse.dbId) throw new Error(`SQLite-WASM-Fallback konnte nicht ge�ffnet werden; OPFS-Fehler: ${errorMessage(opfsError)}`);
    openedFilename = String(fallbackResponse.result?.filename ?? WASM_FALLBACK_DATABASE_FILENAME);
    activeStorageMode = "sqlite-wasm-fallback";
    return { dbId: fallbackResponse.dbId };
  }
}

function errorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error && "result" in error) {
    const result = (error as { result?: { message?: unknown } }).result;
    if (typeof result?.message === "string") return result.message;
  }
  return String(error);
}
async function rawExec(promiser: SQLiteWorkerPromiser, dbId: string, sql: string, bind?: unknown[], returnValue?: "resultRows") {
  return promiser("exec", {
    dbId,
    sql,
    bind,
    rowMode: "object",
    ...(returnValue ? { returnValue } : {})
  });
}

async function exec(sql: string, bind?: unknown[], returnValue?: "resultRows") {
  const promiser = await getPromiser();
  const dbId = await getDbId();
  return rawExec(promiser, dbId, sql, bind, returnValue);
}

function parseRow<T extends RecordWithId>(row: Record<string, unknown>) {
  return JSON.parse(String(row.json)) as T;
}

function updatedAt(record: RecordWithId) {
  return typeof record.updated_at === "string" ? record.updated_at : null;
}

class SQLiteFilter<T extends RecordWithId> {
  constructor(private readonly table: SQLiteTable<T>, private readonly predicate: (item: T) => boolean) {}

  async delete() {
    const rows = await this.table.toArray();
    const ids = rows.filter(this.predicate).map((row) => row.id);
    await Promise.all(ids.map((id) => this.table.delete(id)));
  }
}

class SQLiteTable<T extends RecordWithId> {
  constructor(public readonly name: TableName) {}

  async toArray(): Promise<T[]> {
    const response = await exec(`SELECT json FROM ${this.name} ORDER BY updated_at ASC, id ASC`, undefined, "resultRows");
    return (response.result?.resultRows ?? []).map((row) => parseRow<T>(row));
  }

  async get(id: string): Promise<T | undefined> {
    const response = await exec(`SELECT json FROM ${this.name} WHERE id = ? LIMIT 1`, [id], "resultRows");
    const row = response.result?.resultRows?.[0];
    return row ? parseRow<T>(row) : undefined;
  }

  async put(record: T): Promise<string> {
    await exec(
      `INSERT INTO ${this.name} (id, json, updated_at) VALUES (?, ?, ?)
       ON CONFLICT(id) DO UPDATE SET json = excluded.json, updated_at = excluded.updated_at`,
      [record.id, JSON.stringify(record), updatedAt(record)]
    );
    return record.id;
  }

  async bulkPut(records: T[]): Promise<void> {
    if (!records.length) return;
    const values = records.map(() => "(?, ?, ?)").join(", ");
    const bind = records.flatMap((record) => [record.id, JSON.stringify(record), updatedAt(record)]);
    await exec(
      `INSERT INTO ${this.name} (id, json, updated_at) VALUES ${values}
       ON CONFLICT(id) DO UPDATE SET json = excluded.json, updated_at = excluded.updated_at`,
      bind
    );
  }

  async update(id: string, changes: Partial<T>): Promise<number> {
    const existing = await this.get(id);
    if (!existing) return 0;
    await this.put({ ...existing, ...changes } as T);
    return 1;
  }

  async delete(id: string): Promise<void> {
    await exec(`DELETE FROM ${this.name} WHERE id = ?`, [id]);
  }

  async clear(): Promise<void> {
    await exec(`DELETE FROM ${this.name}`);
  }

  filter(predicate: (item: T) => boolean) {
    return new SQLiteFilter(this, predicate);
  }
}

export class ReadinessDB {
  businessProfile = new SQLiteTable<BusinessProfile>("businessProfile");
  aiTools = new SQLiteTable<AITool>("aiTools");
  aiUseCases = new SQLiteTable<AIUseCase>("aiUseCases");
  riskAssessments = new SQLiteTable<RiskAssessment>("riskAssessments");
  actionItems = new SQLiteTable<ActionItem>("actionItems");
  roleCheckRecords = new SQLiteTable<RoleCheckRecord>("roleCheckRecords");
  literacyRecords = new SQLiteTable<LiteracyRecord>("literacyRecords");
  transparencyChecks = new SQLiteTable<TransparencyCheck>("transparencyChecks");
  generatedDocuments = new SQLiteTable<GeneratedDocument>("generatedDocuments");
  moduleStatuses = new SQLiteTable<ModuleStatus>("moduleStatuses");
  readinessScores = new SQLiteTable<ReadinessScore>("readinessScores");
  privacyGateEvents = new SQLiteTable<PrivacyGateEvent>("privacyGateEvents");
  aiAssistUsage = new SQLiteTable<AIAssistUsage>("aiAssistUsage");
  exportHistory = new SQLiteTable<ExportHistory>("exportHistory");
  importHistory = new SQLiteTable<ImportHistory>("importHistory");
  aiCreditLedger = new SQLiteTable<AICreditLedger>("aiCreditLedger");
  licenseActivations = new SQLiteTable<LicenseActivationCache>("licenseActivations");
  appSettings = new SQLiteTable<AppSettings>("appSettings");

  tables = TABLE_NAMES.map((name) => this[name]);

  async transaction(_mode: "r" | "rw", _tables: Array<SQLiteTable<BaseRecord> | SQLiteTable<BusinessProfile> | SQLiteTable<AITool> | SQLiteTable<AIUseCase> | SQLiteTable<RiskAssessment> | SQLiteTable<ActionItem> | SQLiteTable<RoleCheckRecord> | SQLiteTable<LiteracyRecord> | SQLiteTable<TransparencyCheck> | SQLiteTable<GeneratedDocument> | SQLiteTable<ModuleStatus> | SQLiteTable<ReadinessScore> | SQLiteTable<PrivacyGateEvent> | SQLiteTable<AIAssistUsage> | SQLiteTable<ExportHistory> | SQLiteTable<ImportHistory> | SQLiteTable<AICreditLedger> | SQLiteTable<LicenseActivationCache> | SQLiteTable<AppSettings>>, callback: () => Promise<void>) {
    await exec("BEGIN IMMEDIATE");
    try {
      await callback();
      await exec("COMMIT");
    } catch (error) {
      await exec("ROLLBACK").catch(() => undefined);
      throw error;
    }
  }
}

export const db = new ReadinessDB();

export async function detectStorageMode(): Promise<AppSettings["storage_mode"]> {
  await getDbId();
  return activeStorageMode;
}

export async function ensureSettings() {
  const existing = await db.appSettings.get("settings_default");
  const storageMode = await detectStorageMode();
  if (existing) {
    if (existing.storage_mode !== storageMode) {
      const updated = { ...existing, storage_mode: storageMode, updated_at: now() };
      await db.appSettings.put(updated);
      return updated;
    }
    return existing;
  }
  const timestamp = now();
  const settings: AppSettings = {
    id: "settings_default",
    schema_version: SCHEMA_VERSION,
    created_at: timestamp,
    updated_at: timestamp,
    credits_remaining: 25,
    storage_mode: storageMode
  };
  await db.appSettings.put(settings);
  return settings;
}

export async function getWorkspace(): Promise<Workspace> {
  await ensureSettings();
  const [
    businessProfile,
    aiTools,
    aiUseCases,
    riskAssessments,
    actionItems,
    roleCheckRecords,
    literacyRecords,
    transparencyChecks,
    generatedDocuments,
    moduleStatuses,
    readinessScores,
    privacyGateEvents,
    aiAssistUsage,
    exportHistory,
    importHistory,
    aiCreditLedger,
    appSettings
  ] = await Promise.all([
    db.businessProfile.toArray(),
    db.aiTools.toArray(),
    db.aiUseCases.toArray(),
    db.riskAssessments.toArray(),
    db.actionItems.toArray(),
    db.roleCheckRecords.toArray(),
    db.literacyRecords.toArray(),
    db.transparencyChecks.toArray(),
    db.generatedDocuments.toArray(),
    db.moduleStatuses.toArray(),
    db.readinessScores.toArray(),
    db.privacyGateEvents.toArray(),
    db.aiAssistUsage.toArray(),
    db.exportHistory.toArray(),
    db.importHistory.toArray(),
    db.aiCreditLedger.toArray(),
    db.appSettings.toArray()
  ]);
  return {
    app_version: APP_VERSION,
    schema_version: SCHEMA_VERSION,
    rules_version: RULES_VERSION,
    exported_at: now(),
    businessProfile,
    aiTools,
    aiUseCases,
    riskAssessments,
    actionItems,
    roleCheckRecords,
    literacyRecords,
    transparencyChecks,
    generatedDocuments,
    moduleStatuses,
    readinessScores,
    privacyGateEvents,
    aiAssistUsage,
    exportHistory,
    importHistory,
    aiCreditLedger,
    appSettings
  };
}
export async function replaceWorkspace(workspace: Workspace, filename: string) {
  if (!workspace.schema_version || !Array.isArray(workspace.aiUseCases)) {
    throw new Error("Ung�ltiges Backup: Workspace-Struktur fehlt.");
  }
  await db.transaction("rw", db.tables, async () => {
    await Promise.all(db.tables.map((table) => table.clear()));
    await db.businessProfile.bulkPut(workspace.businessProfile ?? []);
    await db.aiTools.bulkPut(workspace.aiTools ?? []);
    await db.aiUseCases.bulkPut(workspace.aiUseCases ?? []);
    await db.riskAssessments.bulkPut(workspace.riskAssessments ?? []);
    await db.actionItems.bulkPut(workspace.actionItems ?? []);
    await db.roleCheckRecords.bulkPut(workspace.roleCheckRecords ?? []);
    await db.literacyRecords.bulkPut(workspace.literacyRecords ?? []);
    await db.transparencyChecks.bulkPut(workspace.transparencyChecks ?? []);
    await db.generatedDocuments.bulkPut(workspace.generatedDocuments ?? []);
    await db.moduleStatuses.bulkPut(workspace.moduleStatuses ?? []);
    await db.readinessScores.bulkPut(workspace.readinessScores ?? []);
    await db.privacyGateEvents.bulkPut(workspace.privacyGateEvents ?? []);
    await db.aiAssistUsage.bulkPut(workspace.aiAssistUsage ?? []);
    await db.exportHistory.bulkPut(workspace.exportHistory ?? []);
    await db.importHistory.bulkPut([...(workspace.importHistory ?? []), {
      id: uid("import"),
      schema_version: SCHEMA_VERSION,
      created_at: now(),
      updated_at: now(),
      filename,
      imported_at: now()
    }]);
    await db.aiCreditLedger.bulkPut(workspace.aiCreditLedger ?? []);
    await db.appSettings.bulkPut(workspace.appSettings?.length ? workspace.appSettings : [await ensureSettings()]);
  });
}

export async function clearLocalData() {
  await db.transaction("rw", db.tables, async () => {
    await Promise.all(db.tables.map((table) => table.clear()));
  });
  await ensureSettings();
}

export async function downloadFile(filename: string, content: string | Blob, mime: string) {
  const isCsv = mime === "text/csv";
  const blob = content instanceof Blob
    ? content
    : new Blob([isCsv ? `﻿${content}` : content], { type: isCsv ? "text/csv;charset=utf-8" : mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}